<template>
  <div v-if="data" class="h-full">
    <BaseTable
      v-if="data?.documentDetails"
      :headers="tableHeaders"
      :list="data.documentDetails"
      table-id="transactionDetailsTable"
      is-add-new-hidden
      @refresh-data="onRefreshData">
      <template #custom="{ item, header }">
        <BaseInput
          v-if="header === 'documentDetailRowId'"
          :mode="mode"
          color="base"
          class="h-8 bg-white rounded-md mx-1"
          v-model="item[header]"
          :warning="getDetailFieldSoftError(header, item)"
          show-error-as-tooltip />
        <BaseInput
          v-if="
            header === 'qty1' ||
            header === 'qty2' ||
            header === 'qty3' ||
            header === 'discount1' ||
            header === 'discount2' ||
            header === 'discount3' ||
            header === 'discount4' ||
            header === 'discount5' ||
            header === 'discount6' ||
            header === 'vatPercent'
          "
          type="number"
          color="base"
          :min="0"
          :text-right="
            header === 'qty1' ||
            header === 'qty2' ||
            header === 'qty3' ||
            header === 'discount1' ||
            header === 'discount2' ||
            header === 'discount3' ||
            header === 'discount4' ||
            header === 'discount5' ||
            header === 'discount6' ||
            header === 'vatPercent'
          "
          :mode="mode"
          class="h-8 bg-white rounded-md mx-1"
          v-model="item[header]"
          :warning="getDetailFieldSoftError(header, item)"
          show-error-as-tooltip />
        <BaseInput
          v-if="
            header === 'unitPrice' ||
            header === 'grossValue' ||
            header === 'discountValue1' ||
            header === 'discountValue2' ||
            header === 'discountValue3' ||
            header === 'discountValue4' ||
            header === 'discountValue5' ||
            header === 'discountValue6' ||
            header === 'totalDiscountValue' ||
            header === 'netValue' ||
            header === 'vatValue'
          "
          type="number"
          price
          color="base"
          icon="right"
          :mode="mode"
          class="h-8 bg-white rounded-md mx-1"
          v-model="item[header]"
          :warning="getDetailFieldSoftError(header, item)"
          show-error-as-tooltip>
          <template #icon-right>
            <div
              class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
              {{ '€' }}
            </div>
          </template>
        </BaseInput>

        <BaseInput
          v-if="header === 'representativeMaterialAlterCode.materialAlterCode.alterCode.code'"
          color="base"
          readonly
          :mode="mode"
          class="h-8 bg-white rounded-md mx-1"
          show-error-as-tooltip
          :model-value="
            item.representativeMaterialAlterCode?.materialAlterCode?.alterCode?.name ?? ''
          "
          :warning="getDetailFieldSoftError(header, item)"
          @click="openMaterialAlterCodeModal(item)" />

        <BaseInput
          v-if="header === 'representativeMeasurementUnit1Id'"
          color="base"
          readonly
          :mode="mode"
          :model-value="item.representativeMeasurementUnit1?.measurementUnit?.name ?? ''"
          show-error-as-tooltip
          class="h-8 bg-white rounded-md mx-1"
          :warning="getDetailFieldSoftError(header, item)"
          :disabled="!item.representativeMeasurementUnitCode1"
          @click="openMeasurementUnitModal(item, 1)" />

        <BaseInput
          v-if="header === 'representativeMeasurementUnit2Id'"
          color="base"
          readonly
          :model-value="item.representativeMeasurementUnit2?.measurementUnit?.name ?? ''"
          :mode="mode"
          show-error-as-tooltip
          class="h-8 bg-white rounded-md mx-1"
          :warning="getDetailFieldSoftError(header, item)"
          :disabled="!item.representativeMeasurementUnitCode2"
          @click="openMeasurementUnitModal(item, 2)" />

        <BaseInput
          v-if="header === 'representativeMeasurementUnit3Id'"
          color="base"
          readonly
          :model-value="item.representativeMeasurementUnit3?.measurementUnit?.name ?? ''"
          :mode="mode"
          show-error-as-tooltip
          class="h-8 bg-white rounded-md mx-1"
          :warning="getDetailFieldSoftError(header, item)"
          :disabled="!item.representativeMeasurementUnitCode3"
          @click="openMeasurementUnitModal(item, 3)" />
      </template>
    </BaseTable>

    <DCMeasurementUnitModalCreateEdit
      v-if="showMeasurementUnitModal && mode == 'edit'"
      mode="assign"
      :representative-name="data?.representative?.name"
      :measurement-unit="selectedMeasurementUnit"
      @closeModal="showMeasurementUnitModal = false"
      @save="onMeasurementUnitSaved" />

    <DCMaterialAlterCodeModalCreateEdit
      v-if="showMaterialAlterCodeModal && mode === 'edit'"
      mode="assign"
      :material-alter-code="selectedMaterialAlterCode"
      :representativeName="data.representative?.name"
      :existing-material-ids="[]"
      @close-modal="showMaterialAlterCodeModal = false"
      @save="onMaterialAlterCodeSaved" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import BaseTable from '@/components/common/BaseTable.vue';
import DCMeasurementUnitModalCreateEdit from '@/components/traders/DCMeasurementUnitModalCreateEdit.vue';
import BaseInput from '../common/BaseInput.vue';
import DCMaterialAlterCodeModalCreateEdit from './DCMaterialAlterCodeModalCreateEdit.vue';

import type {
  DCDocumentDetails,
  DCDocumentHeader,
  DCMaterialAlterCode,
  DCMeasurementUnit,
} from '@/types/DataCollection';
import { RecordMode } from '@/types/Base';
import { useLookupsStore } from '@/stores/lookups';
import {
  addOrEditDCMaterialAlterCode,
  addOrEditDCMeasurementUnit,
} from '@/services/api/dataCollection';
import { useToastStore } from '@/stores/toast';
const { t: $t } = useI18n();

const props = defineProps<{
  fieldRequirements?: {
    header: Record<string, boolean>;
    details: Record<string, boolean>;
  } | null;
}>();

const data = defineModel<Partial<DCDocumentHeader>>();

const emit = defineEmits<{
  refreshData: [];
  init: [];
}>();

function pascalToCamelCase(str: string): string {
  const camelCase = str.charAt(0).toLowerCase() + str.slice(1);
  return camelCase.replace(/ID$/, 'Id');
}

function getDetailFieldSoftError(tableHeader: string, item: any): string {
  if (!props.fieldRequirements?.details || mode.value === 'view') return '';

  var headerValidators = tableHeaders.find((header) => header.value === tableHeader)?.validators;
  if (!headerValidators) return '';

  for (const fieldName of headerValidators) {
    const pascalFieldName = Object.keys(props.fieldRequirements!.details).find((key) => {
      return pascalToCamelCase(key) === fieldName;
    });
    if (!pascalFieldName || !props.fieldRequirements!.details[pascalFieldName]) continue;

    const fieldValue = item[fieldName];
    const isEmpty =
      fieldValue === null || fieldValue === undefined || fieldValue === '' || fieldValue === 0;
    return isEmpty ? $t('messages.errors.required') : '';
  }

  return '';
}

const tableHeaders = [
  {
    value: 'documentDetailRowId',
    text: $t('documentDetails.rowId'),
    type: 'custom',
    active: true,
    validators: ['documentDetailRowId'],
  },
  {
    value: 'representativeMaterialAlterCodeCode',
    text: $t('transactions.repMaterialCode'),
    type: 'text',
    active: true,
    validators: ['representativeMaterialAlterCodeCode'],
  },
  {
    value: 'representativeMaterialAlterCodeName',
    text: $t('transactions.repMaterialName'),
    type: 'text',
    active: true,
    validators: ['representativeMaterialAlterCodeName', 'representativeMaterialAlterCodeId'],
  },
  {
    value: 'representativeMaterialAlterCode.materialAlterCode.alterCode.code',
    text: $t('documentDetails.materialCode'),
    type: 'custom',
    active: true,
    validators: ['companyMaterialAlterCodeId'],
  },
  {
    value: 'representativeMaterialAlterCode.materialAlterCode.alterCode.name',
    text: $t('documentDetails.materialName'),
    type: 'text',
    active: true,
    validators: ['companyMaterialAlterCodeId'],
  },
  {
    value: 'qty1',
    text: $t('documentDetails.quantity1'),
    type: 'custom',
    active: true,
    validators: ['qty1'],
  },
  {
    value: 'representativeMeasurementUnitCode1',
    text: $t('transactions.repUnitCode1'),
    type: 'text',
    active: true,
    validators: ['representativeMeasurementUnitCode1'],
  },
  {
    value: 'representativeMeasurementUnit1Id',
    text: $t('transactions.repMeasurementUnit1'),
    type: 'custom',
    active: true,
    validators: ['representativeMeasurementUnit1Id', 'companyMeasurementUnit1Id'],
  },
  {
    value: 'qty2',
    text: $t('documentDetails.quantity2'),
    type: 'custom',
    active: true,
    validators: ['qty2'],
  },
  {
    value: 'representativeMeasurementUnitCode2',
    text: $t('transactions.repUnitCode2'),
    type: 'text',
    active: true,
    validators: ['representativeMeasurementUnitCode2'],
  },
  {
    value: 'representativeMeasurementUnit2Id',
    text: $t('transactions.repUnit2'),
    type: 'custom',
    active: true,
    validators: ['representativeMeasurementUnit2Id', 'companyMeasurementUnit2Id'],
  },
  {
    value: 'qty3',
    text: $t('documentDetails.quantity3'),
    type: 'custom',
    active: true,
    validators: ['qty3'],
  },
  {
    value: 'representativeMeasurementUnitCode3',
    text: $t('transactions.repUnitCode3'),
    type: 'text',
    active: true,
    validators: ['representativeMeasurementUnitCode3'],
  },
  {
    value: 'representativeMeasurementUnit3Id',
    text: $t('transactions.repUnit3'),
    type: 'custom',
    active: true,
    validators: ['representativeMeasurementUnit3Id', 'companyMeasurementUnit3Id'],
  },
  {
    value: 'unitPrice',
    text: $t('documentDetails.unitPrice'),
    type: 'custom',
    active: true,
    validators: ['unitPrice'],
  },
  {
    value: 'discount1',
    text: $t('transactions.discount1'),
    type: 'custom',
    active: true,
    validators: ['discount1'],
  },
  {
    value: 'discount2',
    text: $t('transactions.discount2'),
    type: 'custom',
    active: true,
    validators: ['discount2'],
  },
  {
    value: 'discount3',
    text: $t('transactions.discount3'),
    type: 'custom',
    active: true,
    validators: ['discount3'],
  },
  {
    value: 'discount4',
    text: $t('transactions.discount4'),
    type: 'custom',
    active: true,
    validators: ['discount4'],
  },
  {
    value: 'discount5',
    text: $t('transactions.discount5'),
    type: 'custom',
    active: true,
    validators: ['discount5'],
  },
  {
    value: 'discount6',
    text: $t('transactions.discount6'),
    type: 'custom',
    active: true,
    validators: ['discount6'],
  },
  {
    value: 'discountValue1',
    text: $t('transactions.discountValue1'),
    type: 'custom',
    active: true,
    validators: ['discountValue1'],
  },
  {
    value: 'discountValue2',
    text: $t('transactions.discountValue2'),
    type: 'custom',
    active: true,
    validators: ['discountValue2'],
  },
  {
    value: 'discountValue3',
    text: $t('transactions.discountValue3'),
    type: 'custom',
    active: true,
    validators: ['discountValue3'],
  },
  {
    value: 'discountValue4',
    text: $t('transactions.discountValue4'),
    type: 'custom',
    active: true,
    validators: ['discountValue4'],
  },
  {
    value: 'discountValue5',
    text: $t('transactions.discountValue5'),
    type: 'custom',
    active: true,
    validators: ['discountValue5'],
  },
  {
    value: 'discountValue6',
    text: $t('transactions.discountValue6'),
    type: 'custom',
    active: true,
    validators: ['discountValue6'],
  },
  {
    value: 'totalDiscountValue',
    text: $t('transactions.totalDiscountValue'),
    type: 'custom',
    active: true,
    validators: ['totalDiscountValue'],
  },
  {
    value: 'netValue',
    text: $t('documentDetails.netValue'),
    type: 'custom',
    active: true,
    validators: ['netValue'],
  },
  {
    value: 'vatPercent',
    text: $t('documentDetails.vatPercent'),
    type: 'custom',
    active: true,
    validators: ['vatPercent'],
  },
  {
    value: 'vatValue',
    text: $t('transactions.vatValue'),
    type: 'custom',
    active: true,
    validators: ['vatValue'],
  },
  {
    value: 'grossValue',
    text: $t('documentDetails.grossValue'),
    type: 'custom',
    active: true,
    validators: ['grossValue'],
  },
  {
    value: 'isFromManager',
    text: $t('transactions.isFromManager'),
    type: 'check',
    active: true,
    validators: ['isFromManager'],
  },
  {
    value: 'isActive',
    text: $t('baseObject.isActive'),
    type: 'check',
    active: true,
    validators: ['isActive'],
  },
];

const { getMeasurementUnitsLookup } = useLookupsStore();
const showMaterialAlterCodeModal = ref(false);
const showMeasurementUnitModal = ref(false);
const selectedUnitNumber = ref<number>(1);
const selectedMaterialAlterCode = ref<DCMaterialAlterCode>({} as DCMaterialAlterCode);
const selectedMeasurementUnit = ref<DCMeasurementUnit>({} as DCMeasurementUnit);
const selectedRow = ref<DCDocumentDetails>();
const toastStore = useToastStore();

onMounted(async () => {
  await getMeasurementUnitsLookup();
  emit('init');
});

function openMaterialAlterCodeModal(item: DCDocumentDetails) {
  selectedRow.value = item;
  if (item.representativeMaterialAlterCodeId && item.representativeMaterialAlterCode) {
    item.representativeMaterialAlterCode.recordMode = RecordMode.Modified;
    selectedMaterialAlterCode.value = item.representativeMaterialAlterCode;
  } else {
    selectedMaterialAlterCode.value = {
      id: uuidv4(),
      representativeId: data.value?.representative?.id,
      materialAlterCode: {
        id: '',
        alterCode: {
          id: '',
          name: '',
          code: '',
          isActive: true,
          deleted: false,
          isFromManager: true,
        },
        measurementUnit: {
          id: '',
          code: '',
          name: '',
        },
        alterCodeId: uuidv4(),
      },
      materialAlterCodeId: uuidv4(),
      recordMode: RecordMode.Added,
      isActive: true,
      deleted: false,
      createdDate: new Date().toISOString(),
      isFromManager: true,
      materialAlterCodeRemotedCode: item.representativeMaterialAlterCodeCode,
    };
  }
  showMaterialAlterCodeModal.value = true;
}

async function onMaterialAlterCodeSaved(material: DCMaterialAlterCode) {
  if (!data.value) return;
  if (!data.value.documentDetails) return;

  const res = await addOrEditDCMaterialAlterCode([material]);
  if (!res) {
    toastStore.error({ text: res });
    showMaterialAlterCodeModal.value = false;
    return;
  }

  const documentDetailIndex = data.value.documentDetails.findIndex(
    (detail) => detail.id === selectedRow.value?.id
  );

  if (documentDetailIndex !== -1) {
    const documentDetail = data.value.documentDetails[documentDetailIndex];

    documentDetail.representativeMaterialAlterCode = material;
    documentDetail.representativeMaterialAlterCodeId = material.id;
    documentDetail.companyMaterialAlterCodeId = material.materialAlterCodeId;
  }
  toastStore.success({ text: `${$t('common.toast.success')}` });
  showMaterialAlterCodeModal.value = false;
}

const openMeasurementUnitModal = (item: DCDocumentDetails, unitNumber: number) => {
  selectedUnitNumber.value = unitNumber;
  selectedRow.value = item;

  let existingUnit: DCMeasurementUnit | undefined;
  let hasExistingId: string | undefined;
  let existingRemotedCode: string | undefined;
  switch (unitNumber) {
    case 1:
      existingUnit = item.representativeMeasurementUnit1;
      hasExistingId = item.representativeMeasurementUnit1Id;
      existingRemotedCode = item.representativeMeasurementUnitCode1;
      break;
    case 2:
      existingUnit = item.representativeMeasurementUnit2;
      hasExistingId = item.representativeMeasurementUnit2Id;
      existingRemotedCode = item.representativeMeasurementUnitCode2;
      break;
    case 3:
      existingUnit = item.representativeMeasurementUnit3;
      hasExistingId = item.representativeMeasurementUnit3Id;
      existingRemotedCode = item.representativeMeasurementUnitCode3;
      break;
  }

  if (hasExistingId && existingUnit) {
    existingUnit.recordMode = RecordMode.Modified;
    selectedMeasurementUnit.value = existingUnit;
  } else {
    selectedMeasurementUnit.value = {
      id: uuidv4(),
      representativeId: data.value?.representative?.id,
      measurementUnitId: '',
      measurementUnitRemotedCode: existingRemotedCode ?? '',
      recordMode: RecordMode.Added,
      isActive: true,
      deleted: false,
      createdDate: new Date().toISOString(),
      isFromManager: true,
    };
  }
  showMeasurementUnitModal.value = true;
};

async function onMeasurementUnitSaved(measurementUnit: DCMeasurementUnit) {
  if (!data.value || !data.value.documentDetails) return;
  const res = await addOrEditDCMeasurementUnit([measurementUnit]);
  if (!res) {
    toastStore.error({ text: res });
    showMeasurementUnitModal.value = false;
    return;
  }
  const documentDetailIndex = data.value.documentDetails.findIndex(
    (detail) => detail.id === selectedRow.value?.id
  );

  if (documentDetailIndex !== -1) {
    const documentDetail = data.value.documentDetails[documentDetailIndex];
    if (selectedUnitNumber.value == 1) {
      documentDetail.representativeMeasurementUnit1 = measurementUnit;
      documentDetail.representativeMeasurementUnit1Id = measurementUnit.id;
    }
    if (selectedUnitNumber.value == 2) {
      documentDetail.representativeMeasurementUnit2 = measurementUnit;
      documentDetail.representativeMeasurementUnit2Id = measurementUnit.id;
    }
    if (selectedUnitNumber.value == 3) {
      documentDetail.representativeMeasurementUnit3 = measurementUnit;
      documentDetail.representativeMeasurementUnit3Id = measurementUnit.id;
    }
  }
  toastStore.success({ text: `${$t('common.toast.success')}` });
  showMeasurementUnitModal.value = false;
}

const onRefreshData = () => {
  emit('refreshData');
};

const mode = computed<'view' | 'edit'>(() => {
  if (!data.value?.representativeDocumentType) {
    return 'view';
  }
  return data.value.representativeDocumentType.allowChange ? 'edit' : 'view';
});
</script>
